import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tag,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { BsRobot } from "react-icons/bs";
import { FaLock, FaLockOpen } from "react-icons/fa";
import { Header } from "build-ts/metadata";
import { AnalystProto } from "build-ts/analyst";
import { getListOfAnalysts, getAnalyst, updateAnalyst, refreshAnalystDataScope } from "services/AnalystService";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { listPanelScrollSx } from "utils/listScrollAreaStyles";
import { getSortByLabel } from "utils/analysisUtils";
import SplitView from "components/common/SplitView";
import { useAnalystChat } from "context/AnalystChatContext";

type SortField = "LAST_MODIFIED" | "CREATED";

const MemoryAnalystListRow: React.FC<{
  header: Header;
  selected: boolean;
  onSelect: () => void;
  scopeHasInstructions: boolean | undefined;
  timeZone: string;
}> = ({ header, selected, onSelect, scopeHasInstructions, timeZone }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect();
      }
    }}
    borderWidth="1px"
    borderRadius="lg"
    borderColor={selected ? "purple.400" : "purple.200"}
    bg="white"
    px={3}
    py={2.5}
    cursor="pointer"
    _hover={{ borderColor: "purple.500", boxShadow: "sm" }}
    aria-selected={selected}
  >
    <HStack align="start" spacing={2}>
      <Icon as={BsRobot} boxSize={5} color="purple.600" mt={0.5} flexShrink={0} />
      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight="700" color="gray.800" mb={1} noOfLines={2} textAlign="left">
          {header.name || "Unnamed Analyst"}
        </Text>
        <VStack align="stretch" spacing={0.5} alignItems="flex-start">
          <Text fontSize="xs" color="gray.500" mb={0} textAlign="left">
            Last Modified:{" "}
            {header.last_modified_date_ms
              ? `${epochConverterWithTimeZone(header.last_modified_date_ms, "dd-MMM-yyyy, hh:mm a", timeZone)}`
              : "—"}
          </Text>
          <HStack spacing={2} flexWrap="wrap" pt={1}>
            {scopeHasInstructions === undefined ? (
              <Spinner size="xs" color="purple.400" />
            ) : (
              <Tag size="sm" colorScheme={scopeHasInstructions ? "green" : "gray"} borderRadius="md">
                {scopeHasInstructions ? "1" : "0"} instructions
              </Tag>
            )}
          </HStack>
        </VStack>
      </Box>
    </HStack>
  </Box>
);

const MemoryInstructionViewer: React.FC<{
  selectedHeader: Header | null;
  cachedAnalyst: AnalystProto | null;
  onInstructionsSaved: (updatedAnalyst: AnalystProto) => void;
}> = ({ selectedHeader, cachedAnalyst, onInstructionsSaved }) => {
  const [locked, setLocked] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");

  const savedInstructions = cachedAnalyst?.data_scope?.instructions ?? "";
  const loading = !!selectedHeader && !cachedAnalyst;

  useEffect(() => {
    setDraft(cachedAnalyst?.data_scope?.instructions ?? "");
    setLocked(true);
  }, [cachedAnalyst]);

  // Dirty flag is true if the draft is different from the saved instructions
  const dirty = draft !== savedInstructions;

  const handleToggleLock = () => {
    if (!locked && dirty) {
      setDraft(savedInstructions);
    }
    setLocked(!locked);
  };

  const handleSave = async () => {
    if (!cachedAnalyst?.header?.id || !selectedHeader?.id) return;

    setSaving(true);
    try {
      const prevDs = cachedAnalyst?.data_scope ?? {};
      const payload = AnalystProto.create({
        ...cachedAnalyst,
        data_scope: {
          ...prevDs,
          instructions: draft,
        },
      });
      const result = await updateAnalyst(payload, true);
      if (!result) {
        return;
      }
      showToast("Scope instructions saved", "", "success");
      setDraft(result.data_scope?.instructions ?? "");
      setLocked(true);
      onInstructionsSaved(result);
      if (result.header?.id) {
        await refreshAnalystDataScope(result.header.id, true);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!selectedHeader) {
    return (
      <Center h="full" flexDirection="column" gap={3} color="gray.400" px={4}>
        <Icon as={BsRobot} boxSize={10} />
        <Text fontSize="sm" mb={0} textAlign="center">
          Select an analyst to see its instructions.
        </Text>
      </Center>
    );
  }

  return (
    <Flex direction="column" h="full" bg="white">
      <Flex
        px={4}
        py={2.5}
        borderBottomWidth="1px"
        borderColor="gray.100"
        align="center"
        justify="space-between"
        gap={3}
        flexShrink={0}
      >
        <Text fontWeight="700" color="gray.900" fontSize="md" noOfLines={2} mb={0} textAlign="left" flex={1} minW={0}>
          Instructions
        </Text>
        <HStack spacing={2} flexShrink={0}>
          <Tooltip label={locked ? "Unlock to edit" : "Lock (discard unsaved edits)"}>
            <IconButton
              aria-label={locked ? "Unlock to edit" : "Lock editor"}
              icon={<Icon as={locked ? FaLock : FaLockOpen} />}
              size="sm"
              variant="ghost"
              colorScheme="purple"
              onClick={handleToggleLock}
              isDisabled={loading}
            />
          </Tooltip>
          <Button
            size="sm"
            colorScheme="green"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={locked || !dirty || loading}
          >
            Save
          </Button>
        </HStack>
      </Flex>

      <Box flex={1} overflow="hidden" p={4}>
        {loading ? (
          <Center h="full">
            <Spinner color="purple.500" />
          </Center>
        ) : (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            isReadOnly={locked}
            placeholder="No scope instructions yet. Unlock to add instructions."
            height="100%"
            minH="200px"
            resize="none"
            fontSize="sm"
            borderColor="gray.200"
            _readOnly={{ bg: "gray.50", cursor: locked ? "default" : "text" }}
            sx={{
              ...(locked ? { cursor: "default" } : {}),
            }}
          />
        )}
      </Box>
    </Flex>
  );
};

const MemoryConfigurationPanel: React.FC = () => {
  const { userId, defaultAnalystId, defaultTimeZone } = useUser();
  const { analystsList, isLoadingAnalysts } = useAnalystChat();

  const [allHeaders, setAllHeaders] = useState<Header[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortField>("LAST_MODIFIED");
  const [sortAscending, setSortAscending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedHeader, setSelectedHeader] = useState<Header | null>(null);
  const [analystById, setAnalystById] = useState<Map<string, AnalystProto>>(new Map());

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getListOfAnalysts(-1, 1, sortBy, sortAscending, debouncedSearch.trim() || undefined, undefined);
      if (response?.headers) {
        setAllHeaders(response.headers);
      } else {
        setAllHeaders([]);
      }
    } finally {
      setListLoading(false);
    }
  }, [sortBy, sortAscending, debouncedSearch]);

  useEffect(() => {
    if (analystsList.length > 0 && isLoadingAnalysts === false) {
      setAllHeaders(analystsList);
      setListLoading(false);
      return;
    }

    loadList();
  }, [analystsList, isLoadingAnalysts]);

  const { yourAnalysts, othersAnalysts } = useMemo(() => {
    const your = allHeaders.filter((h) => h.author_id === userId || h.id === defaultAnalystId);
    const others = allHeaders.filter((h) => h.author_id !== userId && h.id !== defaultAnalystId);
    return { yourAnalysts: your, othersAnalysts: others };
  }, [allHeaders, userId, defaultAnalystId]);

  const fetchedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ids = allHeaders
      .map((h) => h.id)
      .filter((id): id is string => !!id && !fetchedIds.current.has(id));

    if (ids.length === 0) return;

    ids.forEach((id) => fetchedIds.current.add(id));

    Promise.all(ids.map((id) => getAnalyst(id))).then((analysts) => {
      setAnalystById((prev) => {
        const next = new Map(prev);
        analysts.forEach((a) => {
          if (a?.header?.id) next.set(a.header.id, a);
        });
        return next;
      });
    });
  }, [allHeaders]);

  const onInstructionsSaved = useCallback((updatedAnalyst: AnalystProto) => {
    if (updatedAnalyst.header?.id) {
      setAnalystById((prev) => {
        const next = new Map(prev);
        next.set(updatedAnalyst.header!.id!, updatedAnalyst);
        return next;
      });
    }
  }, []);

  const renderSection = (title: string, headers: Header[]) => {
    if (headers.length === 0) return null;
    return (
      <Box>
        <HStack spacing={3} mb={3}>
          <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
            {title}
          </Text>
          <Tag size="sm" colorScheme="purple" borderRadius="full" fontWeight="600">
            {headers.length}
          </Tag>
          <Divider flex="1" borderColor="gray.200" />
        </HStack>
        <VStack align="stretch" spacing={2}>
          {headers.map((h) => {
            const cached = h.id ? analystById.get(h.id) : undefined;
            const scopeHasInstructions = cached !== undefined
              ? !!(cached.data_scope?.instructions?.trim())
              : undefined;
            return (
              <MemoryAnalystListRow
                key={h.id}
                header={h}
                selected={selectedHeader?.id === h.id}
                onSelect={() => setSelectedHeader(h)}
                scopeHasInstructions={scopeHasInstructions}
                timeZone={defaultTimeZone}
              />
            );
          })}
        </VStack>
      </Box>
    );
  };

  const leftPanel = (
    <Flex direction="column" h="full" bg="white" overflow="hidden">
      <Box px={3} pt={3} pb={2} flexShrink={0} borderBottomWidth="1px" borderColor="gray.200" bg="white">
        <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2} textAlign="left">
          Analysts
        </Text>
        <InputGroup size="sm" mb={2}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search analysts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="white"
            borderColor="gray.200"
          />
        </InputGroup>
        <Flex align="center" gap={2} flexWrap="wrap">
          <Text fontSize="xs" color="gray.600" mb={0} whiteSpace="nowrap">
            Sort by:
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              borderColor="gray.300"
            >
              {getSortByLabel(sortBy)} {sortAscending ? "Asc" : "Desc"}
            </MenuButton>
            <MenuList fontSize="sm">
              <MenuItem onClick={() => { setSortBy("LAST_MODIFIED"); setSortAscending(false); }}>Last Modified (Desc)</MenuItem>
              <MenuItem onClick={() => { setSortBy("LAST_MODIFIED"); setSortAscending(true); }}>Last Modified (Asc)</MenuItem>
              <MenuItem onClick={() => { setSortBy("CREATED"); setSortAscending(false); }}>Created (Desc)</MenuItem>
              <MenuItem onClick={() => { setSortBy("CREATED"); setSortAscending(true); }}>Created (Asc)</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Box>
      <Box flex={1} minH={0} overflowY="auto" px={3} py={3} sx={listPanelScrollSx(true)}>
        {listLoading ? (
          <Center py={10}>
            <Spinner color="purple.500" />
          </Center>
        ) : (
          <VStack align="stretch" spacing={6}>
            {renderSection("Your Analysts", yourAnalysts)}
            {renderSection("Others' Analysts", othersAnalysts)}
            {yourAnalysts.length === 0 && othersAnalysts.length === 0 && (
              <Text fontSize="sm" color="gray.500" textAlign="left">
                No analysts match your filters.
              </Text>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );

  const selectedAnalyst = selectedHeader?.id ? (analystById.get(selectedHeader.id) ?? null) : null;

  return (
    <SplitView
      leftPanel={leftPanel}
      rightPanel={
        <MemoryInstructionViewer
          selectedHeader={selectedHeader}
          cachedAnalyst={selectedAnalyst}
          onInstructionsSaved={onInstructionsSaved}
        />
      }
      isRightPanelVisible
      defaultRightWidth={38}
    />
  );
};

export default MemoryConfigurationPanel;
